import React from 'react';
import { Box, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import {
  ESCALATION_CATALOG,
  instructionOptions,
  operatingHoursDescriptions,
  operatingHoursOptions,
  type EscalationInstruction,
  type OperatingHours,
} from 'skipify-types';

import { ConfigSection } from '../../../common';
import { AgenticEscalationSettings } from '../../../store/modules/merchant/types';
import { useEllaDraft } from '../EllaDraftContext';
import { FORM_MAX_WIDTH, radioSx } from '../ellaFormStyles';

const OPERATING_HOURS_LABELS: Record<OperatingHours, string> = {
  '24/7': '24/7',
  'business-hours': 'Business hours',
  'extended-retail': 'Extended retail',
  'weekends-only': 'Weekends only',
  'by-appointment': 'By appointment',
};

const isOperatingHours = (value: unknown): value is OperatingHours =>
  typeof value === 'string' && (operatingHoursOptions as readonly string[]).includes(value);

const isEscalationInstruction = (value: unknown): value is EscalationInstruction =>
  typeof value === 'string' && (instructionOptions as readonly string[]).includes(value);

const contactHintForInstruction = (instruction: EscalationInstruction): string => {
  if (instruction.includes('call our support phone')) {
    return 'Phone number is recommended for this instruction.';
  }
  if (instruction.includes('email our support') || instruction.includes('follow up by email')) {
    return 'Email is recommended for this instruction.';
  }
  if (instruction.includes('Contact Us link')) {
    return 'Contact URL is recommended for this instruction.';
  }
  return 'Contact details are optional and help the shopping agent hand shoppers off correctly.';
};

export const EscalationForm = (): JSX.Element => {
  const { draft, readOnly, updateDraft } = useEllaDraft();
  const escalation = draft.escalation!;

  const thresholdInput = String(escalation.threshold);
  const parsedThreshold = Number(thresholdInput);
  const isThresholdValid =
    Number.isInteger(parsedThreshold) &&
    parsedThreshold >= ESCALATION_CATALOG.escalationThreshold.min &&
    parsedThreshold <= ESCALATION_CATALOG.escalationThreshold.max;

  const updateEscalation = (patch: Partial<AgenticEscalationSettings>) => {
    updateDraft((prev) => ({
      ...prev,
      escalation: {
        ...prev.escalation!,
        ...patch,
      },
    }));
  };

  return (
    <Box
      component="form"
      id="ella-escalation-form"
      data-testid="ella-escalation-form"
      onSubmit={(e) => e.preventDefault()}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
      }}
    >
      <ConfigSection
        title="Escalation threshold *"
        description={`Required. Monetary amount (${ESCALATION_CATALOG.escalationThreshold.min.toLocaleString()}–${ESCALATION_CATALOG.escalationThreshold.max.toLocaleString()}) in merchant currency above which conversations escalate to human support.`}
        marginBottom={2}
      >
        <TextField
          fullWidth
          type="number"
          value={thresholdInput}
          onChange={(e) => {
            const next = Number(e.target.value);
            if (!Number.isNaN(next)) {
              updateEscalation({ threshold: next });
            }
          }}
          disabled={readOnly}
          error={!isThresholdValid}
          helperText={
            !isThresholdValid
              ? `Enter a whole number between ${ESCALATION_CATALOG.escalationThreshold.min} and ${ESCALATION_CATALOG.escalationThreshold.max}.`
              : undefined
          }
          inputProps={{
            'aria-label': 'Escalation threshold',
            'aria-required': true,
            min: ESCALATION_CATALOG.escalationThreshold.min,
            max: ESCALATION_CATALOG.escalationThreshold.max,
            step: 1,
          }}
          sx={{ maxWidth: FORM_MAX_WIDTH }}
        />
      </ConfigSection>

      <ConfigSection
        title="Operating hours *"
        description="Required. When human support is available for escalations."
        marginBottom={2}
      >
        <RadioGroup
          value={escalation.operatingHours}
          onChange={(_e, value) => {
            if (isOperatingHours(value)) {
              updateEscalation({ operatingHours: value });
            }
          }}
          sx={{ maxWidth: FORM_MAX_WIDTH, gap: 2 }}
        >
          {operatingHoursOptions.map((option) => (
            <Box
              key={option}
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                cursor: readOnly ? 'default' : 'pointer',
              }}
            >
              <Radio
                size="small"
                value={option}
                checked={escalation.operatingHours === option}
                disabled={readOnly}
                sx={{ ...radioSx, mt: '2px' }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  sx={{
                    m: 0,
                    color: 'primary.dark',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 16,
                    fontWeight: 600,
                    lineHeight: 'normal',
                  }}
                >
                  {OPERATING_HOURS_LABELS[option]}
                </Typography>
                <Typography
                  variant="small"
                  color="text.secondary"
                  sx={{ m: 0, lineHeight: '20px' }}
                >
                  {operatingHoursDescriptions[option]}
                </Typography>
              </Box>
            </Box>
          ))}
        </RadioGroup>
      </ConfigSection>

      <ConfigSection
        title="Instruction *"
        description="Required. What the shopping agent should tell the shopper when escalating."
        marginBottom={2}
      >
        <RadioGroup
          value={escalation.instruction}
          onChange={(_e, value) => {
            if (isEscalationInstruction(value)) {
              updateEscalation({ instruction: value });
            }
          }}
          sx={{ maxWidth: FORM_MAX_WIDTH, gap: 2 }}
        >
          {instructionOptions.map((option) => (
            <Box
              key={option}
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                cursor: readOnly ? 'default' : 'pointer',
              }}
            >
              <Radio
                size="small"
                value={option}
                checked={escalation.instruction === option}
                disabled={readOnly}
                sx={{ ...radioSx, mt: '2px' }}
              />
              <Typography
                sx={{
                  m: 0,
                  color: 'primary.dark',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 16,
                  fontWeight: 600,
                  lineHeight: 'normal',
                }}
              >
                {option}
              </Typography>
            </Box>
          ))}
        </RadioGroup>
      </ConfigSection>

      <ConfigSection
        title="Contact details"
        description={`Optional. ${contactHintForInstruction(escalation.instruction)}`}
        marginBottom={2}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: FORM_MAX_WIDTH }}>
          <TextField
            fullWidth
            label="Contact phone number (optional)"
            placeholder="+1 (555) 555-5555"
            value={escalation.contactPhoneNumber ?? ''}
            onChange={(e) => updateEscalation({ contactPhoneNumber: e.target.value })}
            disabled={readOnly}
            inputProps={{ 'aria-label': 'Contact phone number (optional)' }}
          />
          <TextField
            fullWidth
            label="Contact email (optional)"
            placeholder="help@example.com"
            value={escalation.contactEmail ?? ''}
            onChange={(e) => updateEscalation({ contactEmail: e.target.value })}
            disabled={readOnly}
            inputProps={{ 'aria-label': 'Contact email (optional)' }}
          />
          <TextField
            fullWidth
            label="Contact URL (optional)"
            placeholder="https://example.com/contact"
            value={escalation.contactUrl ?? ''}
            onChange={(e) => updateEscalation({ contactUrl: e.target.value })}
            disabled={readOnly}
            inputProps={{ 'aria-label': 'Contact URL (optional)' }}
          />
        </Box>
      </ConfigSection>
    </Box>
  );
};
